import React, { useEffect, useMemo, useState } from 'react';
import {
    Card, Tabs, Tab, Divider, Grid, TextField, IconButton, InputAdornment,
    Button, Box, Typography, useTheme, useMediaQuery,
    Avatar, Chip, Tooltip, Fade, Stack, ToggleButtonGroup, ToggleButton,
    Skeleton, Zoom
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import BeachAccessRounded from '@mui/icons-material/BeachAccessRounded';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import TuneRounded from '@mui/icons-material/TuneRounded';
import PendingActions from '@mui/icons-material/PendingActions';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';

import RequestAttestationModal from './RequestAttestationModal';
import VacationRequestModal from './VacationRequestModal';
import DemandeAttestationCard from './DemandeAttestationCard';
import DemandeVacationCard from './DemandeVacationCard';
import { getAllDemandes } from '../../../../services/DemandeService';
import './styles/CollaborateurPublicView.css';

const normalize = (s = '') =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const isAttestation = d => normalize(d.type) === 'demande attestation';
const isConge = d => normalize(d.type) === 'demande de conge' || normalize(d.type) === 'vacation';

const initials = (p = '', n = '') => `${p?.[0] ?? ''}${n?.[0] ?? ''}`.toUpperCase();

const EmptyState = ({ icon, title, cta }) => (
    <Fade in>
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 2, bgcolor: 'primary.light', color: 'primary.dark' }}>
                {icon}
            </Avatar>
            <Typography fontWeight={700} mb={1}>{title}</Typography>
            {cta}
        </Box>
    </Fade>
);

export default function SettingsCard({ collab }) {
    const [tab, setTab] = useState(0);
    const [code, setCode] = useState(collab.code ?? '');
    const [showCode, setShow] = useState(false);

    const [openAttest, setOpenAtt] = useState(false);
    const [openLeave, setOpenLeave] = useState(false);

    const [demandes, setDemandes] = useState([]);
    const [loading, setLoading] = useState(true);

    const [query, setQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const loadDemandes = async () => {
        setLoading(true);
        try {
            const { data } = await getAllDemandes();
            setDemandes(data.filter(d => d.collaborateur === collab.id));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDemandes();
    }, [collab.id]);

    const counts = useMemo(() => {
        const att = demandes.filter(isAttestation);
        const cg = demandes.filter(isConge);
        const by = (arr, s) => arr.filter(d => normalize(d.statut) === normalize(s)).length;
        return {
            attTotal: att.length,
            cgTotal: cg.length,
            attPending: by(att, 'en attente'),
            cgPending: by(cg, 'en attente'),
            attDone: by(att, 'livrée'),
            cgDone: by(cg, 'livrée'),
            attRefused: by(att, 'refusée'),
            cgRefused: by(cg, 'refusée'),
        };
    }, [demandes]);

    const filtered = useMemo(() => {
        const q = normalize(query);
        const byType = tab === 1 ? isAttestation : tab === 2 ? isConge : () => false;
        const base = demandes.filter(byType);


        const statusMapped = statusFilter === 'accepté' ? 'livrée' : statusFilter;
        const statusNorm = normalize(statusMapped);

        return base
            .filter(d => !q || normalize(d.sousType || d.type || '').includes(q))
            .filter(d => statusNorm === 'all' || normalize(d.statut) === statusNorm);
    }, [demandes, tab, query, statusFilter]);

    const TabLabel = ({ icon, label, badge }) => (
        <Stack direction="row" spacing={1} alignItems="center">
            {icon}
            <span>{label}</span>
            <Chip size="small" label={badge} sx={{ ml: .5 }} />
        </Stack>
    );

    const isCongeTab = tab === 2;

    return (
        <Card
            sx={{
                display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden',
                borderRadius: 4,
                boxShadow: '0 18px 60px rgba(0,0,0,.12)',
                position: 'relative',
                '& .hero': {
                    px: 3, py: 2.25, color: '#fff',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    position: 'relative',
                    overflow: 'hidden'
                }
            }}
        >
            <Box className="hero">
                <Box sx={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    background:
                        'radial-gradient(1000px 240px at 10% -20%, rgba(255,255,255,.22), transparent),' +
                        'radial-gradient(600px 220px at 110% 0%, rgba(255,255,255,.16), transparent)',
                    animation: 'shine 9s linear infinite'
                }} />
                <style>{`
          @keyframes shine {
            0% { opacity: .9; } 50% { opacity: .7; } 100% { opacity: .9; }
          }
        `}</style>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,.22)', color: '#fff', width: 48, height: 48 }}>
                        {initials(collab.prenom, collab.nom) || <PersonOutlined />}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap fontWeight={900} letterSpacing={.3}>
                            {collab?.prenom} {collab?.nom}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: .9 }}>
                            {collab?.fonction || 'Collaborateur'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Actualiser">
                            <IconButton onClick={loadDemandes} sx={{ color: '#fff' }}>
                                <RefreshRounded />
                            </IconButton>
                        </Tooltip>
                        {tab === 1 && (
                            <Zoom in>
                                <Button
                                    variant="contained"
                                    startIcon={<AddRounded />}
                                    onClick={() => setOpenAtt(true)}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,.18)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,.28)' }
                                    }}
                                >
                                    Attestation
                                </Button>
                            </Zoom>
                        )}
                        {tab === 2 && (
                            <Zoom in>
                                <Button
                                    variant="contained"
                                    startIcon={<AddRounded />}
                                    onClick={() => setOpenLeave(true)}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,.18)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,.28)' }
                                    }}
                                >
                                    Congé
                                </Button>
                            </Zoom>
                        )}
                    </Stack>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'background.paper' }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    centered={!isMobile}
                    allowScrollButtonsMobile={isMobile}
                    scrollButtons={isMobile}
                    TabIndicatorProps={{ sx: { height: 3 } }}
                    sx={{
                        px: 1,
                        '& .MuiTabs-flexContainer': {
                            justifyContent: !isMobile ? 'center' : 'initial',
                        },
                    }}
                >
                    <Tab label={<TabLabel icon={<PersonOutlined fontSize="small" />} label="Compte" badge="-" />} />
                    <Tab
                        label={
                            <TabLabel
                                icon={<ArticleOutlined fontSize="small" />}
                                label="Attestations"
                                badge={`${counts.attPending}/${counts.attTotal}`}
                            />
                        }
                    />
                    <Tab
                        label={
                            <TabLabel
                                icon={<BeachAccessRounded fontSize="small" />}
                                label="Congés"
                                badge={`${counts.cgPending}/${counts.cgTotal}`}
                            />
                        }
                    />
                </Tabs>
            </Box>

            <Divider />

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, pr: 1 }}>
                {tab === 0 && (
                    <>
                        <Typography variant="overline" color="text.secondary">Informations</Typography>
                        <Grid container spacing={2} sx={{ mt: .5 }}>
                            {[
                                ['Prénom', 'prenom'],
                                ['Nom', 'nom'],
                                ['Téléphone', 'telephone'],
                                ['Adresse e-mail', 'email'],
                            ].map(([label, key]) => (
                                <Grid item xs={12} md={6} key={key}>
                                    <TextField
                                        label={label}
                                        value={collab[key] ?? ''}
                                        fullWidth slotProps={{ input: { readOnly: true, style: { backgroundColor: '#f7f7f7' } } }}
                                    />
                                </Grid>
                            ))}
                            <Grid item xs={12}>
                                <TextField
                                    type={showCode ? 'text' : 'password'}
                                    label="Code confidentiel"
                                    value={code}
                                    onChange={e => setCode(e.target.value)}
                                    fullWidth slotProps={{ input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton edge="end" onClick={() => setShow(s => !s)}>
                                                    {showCode ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    } }}
                                />
                            </Grid>
                        </Grid>

                        <Box display="flex" justifyContent="flex-end" mt={3}>
                            <Button variant="contained" sx={{ px: 4, borderRadius: 2, fontWeight: 'bold' }}>
                                Sauvegarder
                            </Button>
                        </Box>
                    </>
                )}

                {tab !== 0 && (
                    <Box
                        sx={{
                            position: 'sticky', top: 0, zIndex: 2, mb: 2, pb: 1,
                            bgcolor: 'background.default',
                        }}
                    >
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}
                               alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Rechercher…"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                sx={{ minWidth: 240, backgroundColor: '#fff', borderRadius: 1 }} slotProps={{ input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                } }}
                            />
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={statusFilter}
                                onChange={(_, v) => v && setStatusFilter(v)}
                                sx={{ ml: { sm: 'auto' } }}
                            >
                                <ToggleButton value="all">
                                    <TuneRounded fontSize="small" />&nbsp;Tous
                                </ToggleButton>
                                <ToggleButton value="en attente">
                                    <PendingActions fontSize="small" />&nbsp;En attente
                                </ToggleButton>


                                <ToggleButton value={isCongeTab ? 'accepté' : 'livrée'}>
                                    <CheckCircleRounded fontSize="small" />
                                    &nbsp;{isCongeTab ? 'Accepté' : 'Livrée'}
                                </ToggleButton>

                                <ToggleButton value="refusée">
                                    <CancelRounded fontSize="small" />&nbsp;Refusée
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Box>
                )}

                {tab === 1 && (
                    <>
                        <RequestAttestationModal
                            open={openAttest}
                            onClose={() => setOpenAtt(false)}
                            onSuccess={loadDemandes}
                            collaborateurId={collab.id}
                        />

                        <Box mt={1} sx={{ maxHeight: 440, overflowY: 'auto', pr: 1 }}>
                            {loading ? (
                                <Stack spacing={1.5}>
                                    <Skeleton variant="rounded" height={84} />
                                    <Skeleton variant="rounded" height={84} />
                                    <Skeleton variant="rounded" height={84} />
                                </Stack>
                            ) : filtered.length === 0 ? (
                                <EmptyState
                                    icon={<ArticleOutlined />}
                                    title={query || statusFilter !== 'all'
                                        ? "Aucun résultat pour ces filtres."
                                        : "Aucune demande d’attestation."}
                                    cta={
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddRounded />}
                                            onClick={() => setOpenAtt(true)}
                                            sx={{ mt: 1.5, borderRadius: 2 }}
                                        >
                                            Demander une attestation
                                        </Button>
                                    }
                                />
                            ) : (
                                filtered.map(d => (
                                    <DemandeAttestationCard key={d.id} demande={d} onDeleted={loadDemandes} />
                                ))
                            )}
                        </Box>
                    </>
                )}

                {tab === 2 && (
                    <>
                        <VacationRequestModal
                            open={openLeave}
                            onClose={() => setOpenLeave(false)}
                            collaborateurId={collab.id}
                            onSubmit={loadDemandes}
                        />

                        <Box mt={1} sx={{ maxHeight: 440, overflowY: 'auto', pr: 1 }}>
                            {loading ? (
                                <Stack spacing={1.5}>
                                    <Skeleton variant="rounded" height={84} />
                                    <Skeleton variant="rounded" height={84} />
                                    <Skeleton variant="rounded" height={84} />
                                </Stack>
                            ) : filtered.length === 0 ? (
                                <EmptyState
                                    icon={<BeachAccessRounded />}
                                    title={query || statusFilter !== 'all'
                                        ? "Aucun résultat pour ces filtres."
                                        : "Aucune demande de congé."}
                                    cta={
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddRounded />}
                                            onClick={() => setOpenLeave(true)}
                                            sx={{ mt: 1.5, borderRadius: 2 }}
                                        >
                                            Demander un congé
                                        </Button>
                                    }
                                />
                            ) : (
                                filtered.map(d => (
                                    <DemandeVacationCard key={d.id} demande={d} onDeleted={loadDemandes} />
                                ))
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </Card>
    );
}
